/**
 * PDF Export for Cybersecurity ROI Calculator
 * Generates comprehensive ROI analysis reports
 */

import { ROSIResult } from '@/types/calculator';
import type jsPDF from 'jspdf';

// Extend jsPDF type to include autoTable plugin
interface jsPDFWithPlugin extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

interface ROIPDFExportParams {
  result: ROSIResult;
  inputs: {
    scenario: string;
    investmentName: string;
    implementationCost: number;
    annualCost: number;
    riskMitigation: number;
    industry: string;
    annualRevenue: number;
    breachProbability: number;
    avgBreachCost: number;
    currentMaturity: string;
    productivitySavings: number;
    insuranceReduction: number;
    complianceFrameworks: string[];
  };
}

export async function generateROIPDF(params: ROIPDFExportParams) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const { result, inputs } = params;
  const doc = new jsPDF() as jsPDFWithPlugin;

  // Helper functions
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatYears = (value: number | null) => {
    if (value === null) return 'Never';
    if (value < 1) return `${(value * 12).toFixed(1)} months`;
    return `${value.toFixed(1)} years`;
  };

  const formatIndustry = (industry: string) => {
    return industry.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatMaturity = (maturity: string) => {
    return maturity.charAt(0).toUpperCase() + maturity.slice(1);
  };

  // ============================================================================
  // TITLE PAGE
  // ============================================================================

  // Company branding
  doc.setFontSize(20);
  doc.setTextColor(34, 197, 94); // Green-500 (brand color)
  doc.setFont('helvetica', 'bold');
  doc.text('INVENTIVE HQ', doc.internal.pageSize.width / 2, 50, { align: 'center' });

  // Main Title
  let titleYPos = 70;

  doc.setFontSize(28);
  doc.setTextColor(17, 24, 39); // Gray-900
  doc.setFont('helvetica', 'bold');
  doc.text('Cybersecurity ROI Analysis', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 12;

  doc.setFontSize(18);
  doc.setTextColor(75, 85, 99); // Gray-600
  doc.setFont('helvetica', 'normal');
  doc.text('Security Investment Return Analysis', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  // Investment name box
  titleYPos += 30;

  doc.setFillColor(249, 250, 251); // Gray-50
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 50, 3, 3, 'F');

  titleYPos += 12;

  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Investment:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(inputs.investmentName, 80, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Report Date:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), 80, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Industry:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatIndustry(inputs.industry), 80, titleYPos);

  // Recommendation badge
  titleYPos += 20;

  const badgeColors = {
    APPROVE: { bg: [34, 197, 94], text: [255, 255, 255] },      // Green
    CONSIDER: { bg: [59, 130, 246], text: [255, 255, 255] },    // Blue
    DEFER: { bg: [251, 146, 60], text: [255, 255, 255] },       // Orange
    REJECT: { bg: [239, 68, 68], text: [255, 255, 255] }        // Red
  };

  const badge = badgeColors[result.recommendation];
  doc.setFillColor(badge.bg[0], badge.bg[1], badge.bg[2]);
  doc.roundedRect(60, titleYPos, doc.internal.pageSize.width - 120, 15, 3, 3, 'F');

  doc.setFontSize(14);
  doc.setTextColor(badge.text[0], badge.text[1], badge.text[2]);
  doc.setFont('helvetica', 'bold');
  doc.text(`RECOMMENDATION: ${result.recommendation}`, doc.internal.pageSize.width / 2, titleYPos + 10, { align: 'center' });

  // Key metrics highlight
  titleYPos += 25;

  doc.setDrawColor(34, 197, 94); // Green border
  doc.setLineWidth(2);
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 55, 3, 3, 'S');

  titleYPos += 10;

  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');

  // Split into 3 columns
  const colWidth = (doc.internal.pageSize.width - 60) / 3;
  const startX = 30;

  // Column 1: Year 1 ROI
  doc.text('Year 1 ROI', startX + colWidth / 2, titleYPos, { align: 'center' });
  doc.setFontSize(18);
  doc.setTextColor(result.rosiYear1 > 0 ? 34 : 239, result.rosiYear1 > 0 ? 197 : 68, result.rosiYear1 > 0 ? 94 : 68);
  doc.setFont('helvetica', 'bold');
  doc.text(formatPercentage(result.rosiYear1), startX + colWidth / 2, titleYPos + 10, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  const roi1Desc = doc.splitTextToSize('% return in first year', colWidth - 4);
  doc.text(roi1Desc, startX + colWidth / 2, titleYPos + 18, { align: 'center' });

  // Column 2: Payback Period
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text('Payback Period', startX + colWidth + colWidth / 2, titleYPos, { align: 'center' });
  doc.setFontSize(18);
  doc.setTextColor(59, 130, 246);
  doc.setFont('helvetica', 'bold');
  doc.text(formatYears(result.paybackPeriod), startX + colWidth + colWidth / 2, titleYPos + 10, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  const paybackDesc = doc.splitTextToSize('Until benefits exceed costs', colWidth - 4);
  doc.text(paybackDesc, startX + colWidth + colWidth / 2, titleYPos + 18, { align: 'center' });

  // Column 3: 5-Year NPV
  doc.setFontSize(10);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text('5-Year NPV', startX + colWidth * 2 + colWidth / 2, titleYPos, { align: 'center' });
  doc.setFontSize(18);
  doc.setTextColor(result.npv5Year > 0 ? 34 : 239, result.npv5Year > 0 ? 197 : 68, result.npv5Year > 0 ? 94 : 68);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(result.npv5Year), startX + colWidth * 2 + colWidth / 2, titleYPos + 10, { align: 'center' });
  doc.setFontSize(7);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  const npvDesc = doc.splitTextToSize('Total value after 5 years', colWidth - 4);
  doc.text(npvDesc, startX + colWidth * 2 + colWidth / 2, titleYPos + 18, { align: 'center' });

  // Footer on title page
  const footerY = doc.internal.pageSize.height - 30;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('Analysis based on industry-standard ROSI (Return on Security Investment) methodology', doc.internal.pageSize.width / 2, footerY, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Confidential - For Internal Use Only', doc.internal.pageSize.width / 2, footerY + 6, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.setFont('helvetica', 'normal');
  doc.text('inventivehq.com  |  (866) 903-2097  |  info@inventivehq.com', doc.internal.pageSize.width / 2, footerY + 12, { align: 'center' });

  // ============================================================================
  // PAGE 2: EXECUTIVE SUMMARY
  // ============================================================================

  doc.addPage();
  let yPos = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(34, 197, 94); // Green-500
  doc.text('Cybersecurity ROI Analysis', 20, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text('Generated by Inventive HQ | inventivehq.com', 20, yPos);

  yPos += 3;
  doc.setTextColor(156, 163, 175);
  doc.text(`Report Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, yPos);

  yPos += 15;

  // Executive Summary Section
  doc.setFillColor(34, 197, 94);
  doc.rect(20, yPos - 5, 170, 10, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', 25, yPos);

  yPos += 12;

  // Parse and render executive summary markdown
  const summaryLines = result.executiveSummary.split('\n');
  doc.setTextColor(17, 24, 39);

  summaryLines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;

    // Check if we need a new page
    if (yPos > 260) {
      doc.addPage();
      yPos = 20;
    }

    // H2 headings (##)
    if (trimmed.startsWith('## ')) {
      yPos += 5;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(trimmed.replace('## ', ''), 20, yPos);
      yPos += 8;
    }
    // H3 headings (###)
    else if (trimmed.startsWith('### ')) {
      yPos += 3;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(trimmed.replace('### ', ''), 20, yPos);
      yPos += 7;
    }
    // List items (-)
    else if (trimmed.startsWith('- ')) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const text = trimmed.replace('- ', '');
      // Handle bold text with **
      const parts = text.split(/(\*\*.*?\*\*)/g);
      let xPos = 25;
      parts.forEach(part => {
        if (part.startsWith('**') && part.endsWith('**')) {
          doc.setFont('helvetica', 'bold');
          const boldText = part.slice(2, -2);
          doc.text(boldText, xPos, yPos);
          xPos += doc.getTextWidth(boldText);
          doc.setFont('helvetica', 'normal');
        } else if (part) {
          doc.text(part, xPos, yPos);
          xPos += doc.getTextWidth(part);
        }
      });
      yPos += 6;
    }
    // Regular paragraphs
    else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const wrapped = doc.splitTextToSize(trimmed, 170);
      doc.text(wrapped, 20, yPos);
      yPos += wrapped.length * 5;
    }
  });

  yPos += 10;

  // ============================================================================
  // RISK MITIGATION ANALYSIS
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFillColor(34, 197, 94);
  doc.rect(20, yPos - 5, 170, 10, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Mitigation Analysis', 25, yPos);

  yPos += 12;

  // Calculate ALE
  const currentALE = inputs.avgBreachCost * (inputs.breachProbability / 100);
  const annualRiskSavings = result.benefits.riskReductionAnnual;

  // Create 3-column layout for risk metrics
  const riskMetrics = [
    {
      title: 'Current Risk (ALE)',
      value: `${formatCurrency(currentALE)}/year`,
      description: 'Your Annual Loss Expectancy — the expected financial impact from cyber incidents based on breach probability and average breach cost.'
    },
    {
      title: 'Risk Reduction',
      value: formatPercentage(inputs.riskMitigation),
      description: 'Percentage of current risk that this security investment is expected to mitigate through prevention and detection.'
    },
    {
      title: 'Annual Risk Savings',
      value: formatCurrency(annualRiskSavings),
      description: 'Expected yearly reduction in losses from prevented or mitigated security incidents after this investment.'
    }
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value', 'Explanation']],
    body: riskMetrics.map(m => [m.title, m.value, m.description]),
    theme: 'grid',
    headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 35, halign: 'right', fontStyle: 'bold', fontSize: 11 },
      2: { cellWidth: 95 }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // INVESTMENT PROFILE
  // ============================================================================

  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Investment Profile', 20, yPos);

  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Parameter', 'Value']],
    body: [
      ['Investment Name', inputs.investmentName],
      ['One-Time Implementation Cost', formatCurrency(inputs.implementationCost)],
      ['Annual Recurring Cost', formatCurrency(inputs.annualCost)],
      ['Risk Mitigation Percentage', formatPercentage(inputs.riskMitigation)],
      ['Industry', formatIndustry(inputs.industry)],
      ['Annual Revenue', formatCurrency(inputs.annualRevenue)],
      ['Current Security Maturity', formatMaturity(inputs.currentMaturity)],
      ['Breach Probability', formatPercentage(inputs.breachProbability)],
      ['Average Breach Cost', formatCurrency(inputs.avgBreachCost)],
      ['Productivity Savings', formatCurrency(inputs.productivitySavings)],
      ['Insurance Reduction', formatPercentage(inputs.insuranceReduction)]
    ],
    theme: 'striped',
    headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 80, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // COST vs BENEFITS BREAKDOWN
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Year 1 Cost-Benefit Analysis', 20, yPos);

  yPos += 8;

  // Costs table
  autoTable(doc, {
    startY: yPos,
    head: [['Investment Costs', 'Amount']],
    body: [
      ['One-Time Implementation', formatCurrency(result.costs.oneTime)],
      ['Annual Recurring', formatCurrency(result.costs.annual)],
      ['Personnel Time', formatCurrency(result.costs.personnelCost)]
    ],
    foot: [['Year 1 Total Cost', formatCurrency(result.costs.year1Total)]],
    theme: 'grid',
    headStyles: { fillColor: [239, 68, 68], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [220, 38, 38], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Benefits table
  autoTable(doc, {
    startY: yPos,
    head: [['Annual Benefits', 'Amount']],
    body: [
      ['Risk Reduction', formatCurrency(result.benefits.riskReductionAnnual)],
      ['Additional Benefits', formatCurrency(result.benefits.additionalBenefits)]
    ],
    foot: [['Total Annual Benefit', formatCurrency(result.benefits.totalBenefitYear1)]],
    theme: 'grid',
    headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [22, 163, 74], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // MULTI-YEAR PROJECTION
  // ============================================================================

  if (yPos > 180) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('5-Year Financial Projection', 20, yPos);

  yPos += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const projectionDesc = doc.splitTextToSize(
    'This shows your cumulative Net Present Value (NPV) over 5 years — the total financial value created by this investment after accounting for costs and the time value of money. The line trending upward indicates growing value as annual benefits compound over time.',
    170
  );
  doc.text(projectionDesc, 20, yPos);
  yPos += projectionDesc.length * 4 + 3;

  const projectionData = result.multiYearProjection.map(p => [
    `Year ${p.year}`,
    formatCurrency(p.cost),
    formatCurrency(p.benefit),
    formatCurrency(p.netBenefit),
    formatPercentage(p.roi),
    formatCurrency(p.npv)
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Year', 'Cost', 'Benefit', 'Net Benefit', 'Cumulative ROI', 'NPV']],
    body: projectionData,
    theme: 'grid',
    headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 20, halign: 'center' },
      1: { cellWidth: 30, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 30, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // SENSITIVITY ANALYSIS
  // ============================================================================

  if (yPos > 200) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Sensitivity Analysis', 20, yPos);

  yPos += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const sensitivityDesc = doc.splitTextToSize(
    'Security investments rarely perform exactly as planned. This shows your ROI under different scenarios: Pessimistic (investment underperforms), Conservative (modest results), Expected (your current assumptions), and Optimistic (better than anticipated). Use this to understand your downside risk and upside potential.',
    170
  );
  doc.text(sensitivityDesc, 20, yPos);
  yPos += sensitivityDesc.length * 4 + 3;

  const sensitivityData = result.sensitivityAnalysis.map(s => [
    s.scenario.charAt(0).toUpperCase() + s.scenario.slice(1),
    formatPercentage(s.riskReduction),
    formatPercentage(s.rosi),
    formatCurrency(s.netBenefit),
    formatPercentage(s.probability)
  ]);

  autoTable(doc, {
    startY: yPos,
    head: [['Scenario', 'Risk Reduction', 'ROI', 'Net Benefit', 'Probability']],
    body: sensitivityData,
    theme: 'grid',
    headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 40 },
      1: { cellWidth: 35, halign: 'right' },
      2: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
      3: { cellWidth: 40, halign: 'right' },
      4: { cellWidth: 25, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // THREAT COVERAGE
  // ============================================================================

  if (result.threatCoverage && result.threatCoverage.length > 0) {
    if (yPos > 180) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text('Threat Coverage Analysis', 20, yPos);

    yPos += 5;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(75, 85, 99);
    const threatDesc = doc.splitTextToSize(
      'This shows how your security investment reduces the risk level for specific cyber threats. "Current Risk" represents your current vulnerability to each threat, while "Post-Investment" shows your improved protection level once this investment is deployed. Lower risk levels mean better protection.',
      170
    );
    doc.text(threatDesc, 20, yPos);
    yPos += threatDesc.length * 4 + 3;

    const threatData = result.threatCoverage.map(tc => [
      tc.threat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      tc.currentRisk.replace(/_/g, ' ').toUpperCase(),
      tc.postInvestmentRisk.replace(/_/g, ' ').toUpperCase(),
      formatPercentage(tc.reductionPercent)
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Threat', 'Current Risk', 'Post-Investment', 'Reduction']],
      body: threatData,
      theme: 'grid',
      headStyles: { fillColor: [168, 85, 247], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 35, halign: 'center' },
        2: { cellWidth: 40, halign: 'center' },
        3: { cellWidth: 35, halign: 'right', fontStyle: 'bold' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // ============================================================================
  // METHODOLOGY
  // ============================================================================

  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Methodology & Assumptions', 20, yPos);

  yPos += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);

  const methodology = [
    'This ROI analysis uses the Return on Security Investment (ROSI) methodology to evaluate the financial',
    'impact of cybersecurity investments. The calculation considers both direct risk reduction and additional',
    'benefits such as compliance, productivity, and insurance cost savings.',
    '',
    'Key Calculations:',
    '• Annual Loss Expectancy (ALE) = Breach Cost × Breach Probability',
    '• Risk Reduction Value = ALE × Risk Mitigation Percentage',
    '• Year 1 ROI = (Benefits - Costs) / Costs × 100',
    '• Net Present Value uses 8% discount rate for multi-year projections',
    '• Payback Period calculated based on cumulative net benefits',
    '',
    'Assumptions:',
    '• Personnel costs calculated at $75/hour loaded rate',
    '• Compliance benefits valued based on audit and certification cost avoidance',
    '• Insurance reductions based on typical cyber insurance premium savings',
    '• Time to effectiveness affects Year 1 risk reduction realization',
    '',
    'DISCLAIMER: This analysis is for planning purposes only. Actual ROI may vary based on implementation',
    'quality, threat landscape changes, and organizational factors. Consult with cybersecurity professionals',
    'for guidance specific to your environment.'
  ];

  methodology.forEach(line => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(line, 20, yPos);
    yPos += 4;
  });

  // ============================================================================
  // ABOUT INVENTIVE HQ
  // ============================================================================

  doc.addPage();
  yPos = 20;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Section header
  doc.setFillColor(34, 197, 94);
  doc.rect(20, yPos - 5, 170, 12, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('About Inventive HQ', 25, yPos + 3);

  yPos += 20;

  // Company description
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');

  const aboutText = [
    'Inventive HQ is a leading cybersecurity and IT services provider specializing in protecting businesses',
    'from cyber threats. We combine advanced technology, proven methodologies, and expert guidance to',
    'help organizations of all sizes build resilient security programs and maximize ROI on security investments.',
    '',
    'Our mission is to make enterprise-grade cybersecurity accessible and affordable for growing businesses.'
  ];

  aboutText.forEach(line => {
    doc.text(line, 20, yPos);
    yPos += 4.5;
  });

  yPos += 8;

  // Services section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Our Services', 20, yPos);

  yPos += 8;

  const serviceColumns = [
    {
      title: 'Security Services',
      services: [
        { name: 'Virtual CISO (vCISO)', url: 'https://inventivehq.com/services/vciso' },
        { name: 'Risk Assessment', url: 'https://inventivehq.com/services/compliance' },
        { name: 'Penetration Testing', url: 'https://inventivehq.com/services/penetration-testing' },
        { name: 'Security Awareness Training', url: 'https://inventivehq.com/services/security-awareness' },
        { name: 'Incident Response', url: 'https://inventivehq.com/services/incident-response' },
        { name: 'Vendor Risk Management', url: 'https://inventivehq.com/services/vendor-risk-management' }
      ]
    },
    {
      title: 'Compliance Services',
      services: [
        { name: 'HIPAA Compliance', url: 'https://inventivehq.com/services/compliance/hipaa' },
        { name: 'SOC 2 Compliance', url: 'https://inventivehq.com/services/compliance/soc2' },
        { name: 'NIST Compliance', url: 'https://inventivehq.com/services/compliance/nist' },
        { name: 'PCI-DSS Compliance', url: 'https://inventivehq.com/services/compliance/pci-dss' }
      ]
    },
    {
      title: 'Managed Services',
      services: [
        { name: '24/7 Detection & Response', url: 'https://inventivehq.com/services/24-7-detection-and-response' },
        { name: 'SOC Services', url: 'https://inventivehq.com/services/24-7-detection-and-response' },
        { name: 'Backup & DR', url: 'https://inventivehq.com/services/backup-and-ransomware-defense' },
        { name: 'Vulnerability Management', url: 'https://inventivehq.com/services/vulnerability-management' }
      ]
    },
    {
      title: 'Cloud Solutions',
      services: [
        { name: 'Cloud Migration', url: 'https://inventivehq.com/services/cloud-infrastructure-management' },
        { name: 'Cloud Optimization', url: 'https://inventivehq.com/services/cloud-infrastructure-management' },
        { name: 'Multi-Cloud Strategy', url: 'https://inventivehq.com/services/cloud-infrastructure-management' }
      ]
    }
  ];

  const columnWidth = 42;
  const columnGap = 4;
  const servicesStartX = 20;

  serviceColumns.forEach((column, colIndex) => {
    const colX = servicesStartX + (colIndex * (columnWidth + columnGap));
    let colY = yPos;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(75, 79, 88);
    doc.text(column.title, colX, colY);

    colY += 5;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(34, 197, 94);

    column.services.forEach((service) => {
      doc.textWithLink(service.name, colX, colY, { url: service.url });
      colY += 3.5;
    });
  });

  const maxServicesCount = Math.max(...serviceColumns.map(col => col.services.length));
  yPos += 5 + (maxServicesCount * 3.5) + 8;

  // Why Choose Us
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Why Choose Inventive HQ?', 20, yPos);

  yPos += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const whyChoose = [
    'Protecting organizations across healthcare, finance, legal, and technology sectors',
    'Vendor-Agnostic Expertise - We recommend the best solutions for your needs',
    'Rapid Response - 24/7 security operations center with under 15 minute response times',
    'Transparent Pricing - No hidden fees, clear ROI, and flexible engagement models'
  ];

  whyChoose.forEach(item => {
    doc.setFillColor(34, 197, 94);
    doc.circle(22, yPos - 1, 1, 'F');

    const wrapped = doc.splitTextToSize(item, 165);
    doc.text(wrapped, 28, yPos);
    yPos += (wrapped.length * 4) + 3;
  });

  // CTA at bottom
  const ctaHeight = 52;
  const ctaOffset = 16 + (pageHeight * 0.05);
  const availableSpace = pageHeight - ctaOffset - yPos;

  if (availableSpace < ctaHeight) {
    doc.addPage();
    yPos = 20;
  } else {
    yPos = pageHeight - ctaHeight - ctaOffset;
  }

  doc.setFillColor(34, 197, 94);
  doc.roundedRect(20, yPos, 170, ctaHeight, 3, 3, 'F');

  yPos += 10;

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('Ready to Implement This Investment?', pageWidth / 2, yPos, { align: 'center' });

  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Schedule a free consultation to discuss implementation, vendor selection, and', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;

  doc.text('achieving your projected ROI.', pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Contact: inventivehq.com/contact', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;
  doc.text('Phone: (866) 903-2097', pageWidth / 2, yPos, { align: 'center' });

  yPos += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('2305 Historic Decatur Rd, Suite 100', pageWidth / 2, yPos, { align: 'center' });

  yPos += 4;
  doc.text('San Diego, CA 92106', pageWidth / 2, yPos, { align: 'center' });

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      'Generated by Inventive HQ Cybersecurity ROI Calculator | inventivehq.com',
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 6,
      { align: 'center' }
    );
  }

  // ============================================================================
  // SAVE THE PDF
  // ============================================================================

  const fileName = `ROI-Analysis-${inputs.investmentName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
